import { apiClient, type GetParams } from '@/api/client'
import {
  extractData,
  extractWrapped,
  extractArray,
  extractPagy,
} from '@/api/responseExtractors'
import type { PagyMeta } from '@/api/responseExtractors'
import {
  mapBulkImportFromApi,
  mapBulkImportErrorRowFromApi,
  mapBulkExportFromApi,
} from '@/types/bulkImport'
import type { BulkCentralSortDirection, BulkExportListItem, BulkExportSortField, BulkImport, BulkImportCentralSortField, BulkImportEntity, BulkImportErrorRow, BulkImportMode, BulkImportStatus, BulkImportTemplateEntity } from '@/types/bulkImport'
import { downloadAuthenticatedFile, fetchAuthenticatedBlob } from '@/utils/downloadAuthenticatedFile'
import type { AuthenticatedBlobResult } from '@/utils/downloadAuthenticatedFile'

import {
  bulkExportsPath,
  bulkImportBasePath,
  bulkImportExportFileNames,
  bulkImportTemplateFileNames,
  combinedImportsPath,
} from './constants'

export async function createImportRequest(
  entity: BulkImportEntity,
  file: File,
  operationType: BulkImportMode = 'create',
): Promise<BulkImport> {
  const fd = new FormData()
  fd.append('operation_type', operationType)
  fd.append('file', file)

  // upload com timeout dedicado de 30s (o timeout global de 5min é para as demais chamadas).
  const envelope = await apiClient.post(bulkImportBasePath(entity), fd, { timeout: 30_000 })
  const data = extractData(envelope)
  const raw = extractWrapped<unknown>(data, 'bulk_import')
  return mapBulkImportFromApi(raw)
}

export async function validateImportRequest(
  entity: BulkImportEntity,
  id: string,
): Promise<BulkImport> {
  const path = `${bulkImportBasePath(entity)}/${id}/validate`
  const envelope = await apiClient.post(path, {})
  const data = extractData(envelope)
  const raw = extractWrapped<unknown>(data, 'bulk_import')
  return mapBulkImportFromApi(raw)
}

export async function previewImportRequest(
  entity: BulkImportEntity,
  id: string,
): Promise<BulkImport> {
  const path = `${bulkImportBasePath(entity)}/${id}/preview`
  const envelope = await apiClient.post(path, {})
  const data = extractData(envelope)
  const raw = extractWrapped<unknown>(data, 'bulk_import')
  return mapBulkImportFromApi(raw)
}

export async function confirmImportRequest(
  entity: BulkImportEntity,
  id: string,
): Promise<BulkImport> {
  const path = `${bulkImportBasePath(entity)}/${id}/confirm`
  const envelope = await apiClient.post(path, {})
  const data = extractData(envelope)
  const raw = extractWrapped<unknown>(data, 'bulk_import')
  return mapBulkImportFromApi(raw)
}

export async function fetchImportStatusRequest(
  entity: BulkImportEntity,
  id: string,
): Promise<BulkImport> {
  const path = `${bulkImportBasePath(entity)}/${id}`
  const envelope = await apiClient.get(path)
  const data = extractData(envelope)
  const raw = extractWrapped<unknown>(data, 'bulk_import')
  return mapBulkImportFromApi(raw)
}

export type BulkImportStatusFilter = BulkImportStatus | 'completed_partial'

export interface FetchImportsParams {
  page?: number
  status?: BulkImportStatusFilter[]
  operationType?: string[]
  author?: string[]
  q?: string
  createdAtStart?: string
  createdAtEnd?: string
  orderBy?: BulkImportCentralSortField
  orderDir?: BulkCentralSortDirection
}

export interface CombinedFetchImportsParams extends FetchImportsParams {
  entity?: BulkImportEntity[]
}

export async function fetchCombinedImportsRequest(
  params?: CombinedFetchImportsParams,
): Promise<{ items: BulkImport[]; pagination: PagyMeta }> {
  const queryParams: GetParams = {}
  if (params?.page !== undefined) queryParams.page = String(params.page)
  if (params?.entity?.length) queryParams.entity = params.entity
  if (params?.status?.length) queryParams.status = params.status
  if (params?.operationType?.length) queryParams.operation_type = params.operationType
  if (params?.author?.length) queryParams.author = params.author
  if (params?.q !== undefined) queryParams.q = params.q
  if (params?.createdAtStart !== undefined) queryParams.created_at_start = params.createdAtStart
  if (params?.createdAtEnd !== undefined) queryParams.created_at_end = params.createdAtEnd
  if (params?.orderBy !== undefined) queryParams.order_by = params.orderBy
  if (params?.orderDir !== undefined) queryParams.order_dir = params.orderDir

  const envelope = await apiClient.get(combinedImportsPath, { params: queryParams })
  const data = extractData(envelope)
  const rawItems = extractArray<unknown>(data, ['bulk_imports', 'imports', 'items'])
  const items = rawItems.map(mapBulkImportFromApi)
  const pagination = extractPagy(envelope.meta)
  return { items, pagination }
}

export async function fetchImportErrorsRequest(
  entity: BulkImportEntity,
  id: string,
  page = 1,
  statuses?: string[],
): Promise<{ rows: BulkImportErrorRow[]; pagination: PagyMeta }> {
  const query = [`page=${page}`]
  if (statuses?.length) {
    for (const status of statuses) query.push(`statuses[]=${status}`)
  }
  const path = `${bulkImportBasePath(entity)}/${id}/errors?${query.join('&')}`
  const envelope = await apiClient.get(path)
  const data = extractData(envelope)
  const rawRows = extractArray<unknown>(data, ['errors', 'rows', 'items'])
  const pagination = extractPagy(envelope.meta)
  const rows = rawRows.map(mapBulkImportErrorRowFromApi)
  return { rows, pagination }
}

export async function downloadImportTemplateFile(entity: BulkImportTemplateEntity): Promise<void> {
  const path = `${bulkImportBasePath(entity)}/template`
  await downloadAuthenticatedFile(path, bulkImportTemplateFileNames[entity])
}

export async function downloadImportReportFile(
  entity: BulkImportEntity,
  id: string,
  kind: 'error' | 'result',
  format: 'xlsx' | 'csv',
): Promise<void> {
  const path = `${bulkImportBasePath(entity)}/${id}/reports/${kind}?format=${format}`
  await downloadAuthenticatedFile(path, `relatorio_${kind}.${format}`)
}

export async function exportPrefilledRequest(
  entity: BulkImportEntity,
  uuids: string[],
): Promise<void> {
  const path = `${bulkImportBasePath(entity)}/export`
  await downloadAuthenticatedFile(path, bulkImportExportFileNames[entity], {
    method: 'POST',
    data: { uuids },
  })
}

export async function exportPrefilledBlobRequest(
  entity: BulkImportEntity,
  uuids: string[],
): Promise<AuthenticatedBlobResult> {
  const path = `${bulkImportBasePath(entity)}/export`
  return fetchAuthenticatedBlob(path, { method: 'POST', data: { uuids } })
}

export interface FetchBulkExportsParams {
  page?: number
  entity?: BulkImportEntity
  author?: string
  createdAtStart?: string
  createdAtEnd?: string
  orderBy?: BulkExportSortField
  orderDir?: BulkCentralSortDirection
}

export async function fetchBulkExportsRequest(
  params?: FetchBulkExportsParams,
): Promise<{ items: BulkExportListItem[]; pagination: PagyMeta }> {
  const queryParams: Record<string, string> = {}
  if (params?.page !== undefined) queryParams.page = String(params.page)
  if (params?.entity !== undefined) queryParams.entity = params.entity
  if (params?.author !== undefined) queryParams.author = params.author
  if (params?.createdAtStart !== undefined) queryParams.created_at_start = params.createdAtStart
  if (params?.createdAtEnd !== undefined) queryParams.created_at_end = params.createdAtEnd
  if (params?.orderBy !== undefined) queryParams.order_by = params.orderBy
  if (params?.orderDir !== undefined) queryParams.order_dir = params.orderDir

  const envelope = await apiClient.get(bulkExportsPath, { params: queryParams })
  const data = extractData(envelope)
  const rawItems = extractArray<unknown>(data, ['bulk_exports', 'items'])
  const items = rawItems.map(mapBulkExportFromApi)
  const pagination = extractPagy(envelope.meta)
  return { items, pagination }
}

export async function downloadBulkExportFile(item: BulkExportListItem): Promise<void> {
  const path = `${bulkExportsPath}/${item.id}/download`
  await downloadAuthenticatedFile(path, bulkImportExportFileNames[item.entity], { method: 'POST' })
}
